// Eskom PARCC — measured frame for recharts charts.
//
// recharts' ResponsiveContainer measures its parent box; on the first paint a
// flex/grid cell can still report a 0/negative size, which makes recharts log
// "The width(-1) and height(-1) of chart should be greater than 0 …" and — under
// React 19 — can throw while it tries to lay out text at degenerate dimensions
// (the Dashboard "Something went wrong" / minified error #31). Even once the box
// is sized, ResponsiveContainer does its own first measurement pass at -1 before
// its observer fires, so it logs the warning regardless of any outer gating.
//
// This frame side-steps ResponsiveContainer entirely: it holds the chart's box,
// watches it with a ResizeObserver, and hands the child a CONCRETE pixel size via
// a render prop — only once the box actually has a positive width AND height. The
// child sizes the recharts chart numerically (no percentages, no measurement), so
// recharts never sees a non-positive dimension. `minWidth: 0` lets the box shrink
// correctly inside flex. Resizes flow through the observer → a fresh size → a
// re-render, so the chart stays responsive without ResponsiveContainer.
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';

export interface ChartSize {
  width: number;
  height: number;
}

// Positive size used where the box can't be measured (jsdom/SSR — no layout, no
// ResizeObserver). Keeps charts rendering in tests/SSR instead of vanishing.
const FALLBACK: ChartSize = { width: 320, height: 200 };

export function ChartFrame({
  children,
  style,
}: {
  /** Render prop — invoked with the measured pixel size once the box is positive. */
  children: (size: ChartSize) => ReactNode;
  style?: CSSProperties;
}): React.JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<ChartSize | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // No ResizeObserver (jsdom/SSR/old runtime): the box never lays out, so fall
    // back to a positive size and render. Gating only matters in a real browser,
    // where ResizeObserver always exists and the 0-size first paint is the bug.
    if (typeof ResizeObserver === 'undefined') {
      setSize({
        width: el.clientWidth || FALLBACK.width,
        height: el.clientHeight || FALLBACK.height,
      });
      return;
    }
    const measure = (): void => {
      const width = el.clientWidth;
      const height = el.clientHeight;
      // Only commit a POSITIVE box; a 0-sized first paint leaves `size` null so the
      // child (and recharts) never mounts at a degenerate dimension.
      setSize((prev) => {
        if (width > 0 && height > 0) {
          if (prev && prev.width === width && prev.height === height) return prev;
          return { width, height };
        }
        return prev;
      });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={ref} style={{ width: '100%', height: '100%', minWidth: 0, ...style }}>
      {size ? children(size) : null}
    </div>
  );
}
